import { useEffect, useRef, useState } from "react";
import Graph from "./component/Org/graph";
import Inputer from "./component/Org/inputer";
import { useDrawGraph, GRAPH_SCALE_SPACE } from "./hooks/useDrawGraph";
import { pointsToLine } from "./domain/lineDomain";
import { loginLog, logAction } from "./domain/logDomain";
import { Modal } from "./component/Org/modal";
import { AXIS_ZOOM_MODE, getHoveredAxis } from "./utils/axisZoom";

function App() {
  const canvasRef = useRef(null);
  const graphRef = useRef(null);
  const legendRef = useRef(null);
  const userData = JSON.parse(localStorage.getItem("userData"))
  const [leftBottom, setLeftBottom] = useState(userData?.leftBottom ?? { x: -5.5, y: -5 });
  const [rightTop, setRightTop] = useState(userData?.rightTop ??{ x: 100, y: 50 });
  const setLimitRightTop = (pos)=>{
    const formatedPos = {x:pos.x,y:pos.y}
    if(Math.log10(pos.x) > 100){
      formatedPos.x = 10**100
    }
    if(Math.log10(pos.x) < -100){
      formatedPos.x = 10**-100
    }
    if(Math.log10(pos.y) > 100){
      formatedPos.y = 10**100
    }
    if(Math.log10(pos.y) < -100){
      formatedPos.y = 10**-100
    }
    setRightTop(formatedPos)
  }
  const setLimitLeftBottom = (pos)=>{
    const formatedPos = {x:pos.x,y:pos.y}
    if(Math.log10(pos.x) > 100){
      formatedPos.x = 10**100
    }
    if(Math.log10(pos.x) < -100  && pos.x > 0){
      formatedPos.x = 10**-100
    }
    if(Math.log10(-pos.x) > 100){
      formatedPos.x = -(10**100)
    }
    if(Math.log10(pos.y) > 100){
      formatedPos.y = 10**100
    }
    if(Math.log10(pos.y) < -100 && pos.y > 0){
      formatedPos.y = 10**-100
    }
    if(Math.log10(-pos.y) > 100){
      formatedPos.y = -(10**100)
    }
    setLeftBottom(formatedPos)
  }
  const [xLabel, setXLabel] = useState(userData?.xLabel ?? {
    x: "x",
    label: "x軸のラベル",
    unit: "m/s",
    isLog:false
  });
  const [yLabel, setYLabel] = useState(userData?.yLabel ?? {
    y: "y",
    label: "y軸のラベル",
    unit: "m",
    isLog:false
  });
  const [datas, setDatas] = useState(userData?.data ?? [
    {
      name: "系列1",
      color: "000000",
      points: [
        { x: 10, y: 20 },
        { x: 20, y: 30 },
        { x: 30, y: 45 },
        { x: 15, y: 35 },
      ],
      line:pointsToLine([
        { x: 10, y: 20 },
        { x: 20, y: 30 },
        { x: 30, y: 45 },
        { x: 15, y: 35 },
      ]),
      isLine:true
    },
    {
      name: "系列2",
      color: "ff0000",
      points: [
        { x: 15, y: 25 },
        { x: 25, y: 35 },
        { x: 35, y: 45 },
        { x: 10, y: 35 },
      ],
      line:pointsToLine([
        { x: 15, y: 25 },
        { x: 25, y: 35 },
        { x: 35, y: 45 },
        { x: 10, y: 35 },
      ]),
      isLine:true
    },
  ]);
  const [legendState, setLegendState] = useState(userData?.legend ??{
    x: 20,
    y: 20,
    visible: true,
    fontSize:16
  });
  const [height,setHeight] = useState(userData?.height ?? 100);
  const [dragging, setDragging] = useState(false);
  const [hoveredAxis, setHoveredAxis] = useState(null);
  const legendDragging = useRef(false);
  const legendDragStart = useRef({ x: 0, y: 0 });
  const legendStartState = useRef(legendState);
  const dragStart = useRef({ x: 0, y: 0 });
  const viewStart = useRef({
    leftBottom,
    rightTop,
  });
  const [zoomVelosity,setZoomVelosity] = useState(userData?.zoomVelosity ?? 3)
  const [axisFontSize,setAxisFontSize] = useState(userData?.fontSize ?? 18)
  const recordLog = (action, payload = {}) => {
    logAction(action, payload);
  };
  const onPointerDown = (e) => {
    // detect whether user started dragging on the legend element
    const legendElem =
      e.target && e.target.closest && e.target.closest("[data-legend]");
    if (legendElem) {
      legendDragging.current = true;
      legendDragStart.current = { x: e.clientX, y: e.clientY };
      legendStartState.current = legendState;
      setDragging(true);
      return;
    }

    setDragging(true);

    dragStart.current = {
      x: e.clientX,
      y: e.clientY,
    };

    viewStart.current = {
      leftBottom,
      rightTop,
    };
  };
  const updateHoveredAxis = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;

    const plotArea = {
      left: GRAPH_SCALE_SPACE,
      right: rect.width,
      top: 0,
      bottom: rect.height - GRAPH_SCALE_SPACE,
    };

    setHoveredAxis(getHoveredAxis(mouseX, mouseY, plotArea));
  };

  const onPointerMove = (e) => {
    updateHoveredAxis(e);
    if (!dragging) return;
    // If legend is being dragged, move legend instead of panning
    if (legendDragging.current) {
      const dx = e.clientX - legendDragStart.current.x;
      const dy = e.clientY - legendDragStart.current.y;

      // If legend was anchored to left (x >= 0), move left offset; if anchored to right (x < 0), adjust right offset inversely
      const start = legendStartState.current;
      const newX = start.x >= 0 ? start.x + dx : start.x - dx;
      const newY = start.y >= 0 ? start.y + dy : start.y - dy;
      // clamp to graph container bounds so legend can't be dragged outside
      const container = graphRef.current;
      if (container) {
        const maxX = container.clientWidth - 20;
        const minX = -container.clientWidth + 20;
        const maxY = container.clientHeight - 20;
        const minY = -container.clientHeight + 20;
        const legendWidth = legendRef.current?.clientWidth ?? 0;
        const legendHeight = legendRef.current?.clientHeight ?? 0;
        const clampedX = Math.max(minX, Math.min(maxX, newX));
        const clampedY = Math.max(minY, Math.min(maxY, newY));
        setLegendState((prev) => ({
          ...prev,
          x: Math.min(
            Math.max(clampedX, 0),
            graphRef.current.clientWidth - legendWidth - 97,
          ),
          y: Math.min(
            Math.max(clampedY, 0),
            graphRef.current.clientHeight*height/100 - legendHeight - 80,
          ),
        }));
      } else {
        setLegendState((prev) => ({
          ...prev,
          x: Math.max(newX, 0),
          y: Math.max(newY, 0),
        }));
      }
      return;
    }

    const dx = e.clientX - dragStart.current.x;
    const dy = e.clientY - dragStart.current.y;

    const xConverter = xLabel.isLog ? Math.log10 : (x)=>x
    const yConverter = yLabel.isLog ? Math.log10 : (y)=>y
    const xRevConverter = xLabel.isLog ? (x)=>10**x : (x)=>x
    const yRevConverter = yLabel.isLog ? (y)=>10**y : (y)=>y

    // World座標へ変換
    const worldDx =
      (dx * (xConverter(rightTop.x) - xConverter(leftBottom.x))) / graphRef.current.clientWidth;

    const worldDy =
      (dy * (yConverter(rightTop.y) - yConverter(leftBottom.y))) / graphRef.current.clientHeight;


    setLimitLeftBottom({
      x: xRevConverter(xConverter(viewStart.current.leftBottom.x) - worldDx),
      y: yRevConverter(yConverter(viewStart.current.leftBottom.y) + worldDy),
    });

    setLimitRightTop({
      x: xRevConverter(xConverter(viewStart.current.rightTop.x) - worldDx),
      y: yRevConverter(yConverter(viewStart.current.rightTop.y) + worldDy),
    });
  };
  const onPointerUp = () => {
    setDragging(false);
    legendDragging.current = false;
  };

  const onPointerLeave = () => {
    setHoveredAxis(null);
  };

  const onWheel = (e) => {
    e.preventDefault();

    const rate = e.deltaY > 0 ? (1 + zoomVelosity/100) : (1 - zoomVelosity/100);

    const XConvert = xLabel.isLog ? Math.log10 : (x)=>x
    const XRevConvert = xLabel.isLog ? (x)=>10**x : (x)=>x
    const YConvert = yLabel.isLog ? Math.log10 : (y)=>y
    const YRevConvert = yLabel.isLog ? (y)=>10**y : (y)=>y

    const centerX = (XConvert(leftBottom.x) + XConvert(rightTop.x)) / 2;
    const centerY = (YConvert(leftBottom.y) + YConvert(rightTop.y)) / 2;

    const halfWidth = ((XConvert(rightTop.x) - XConvert(leftBottom.x)) * rate) / 2;
    const halfHeight = ((YConvert(rightTop.y) - YConvert(leftBottom.y)) * rate) / 2;

    const activeAxis = hoveredAxis ?? AXIS_ZOOM_MODE.BOTH;
    const doZoomX = activeAxis === AXIS_ZOOM_MODE.X || activeAxis === AXIS_ZOOM_MODE.BOTH;
    const doZoomY = activeAxis === AXIS_ZOOM_MODE.Y || activeAxis === AXIS_ZOOM_MODE.BOTH;

    const nextLeftBottom = {
      x: doZoomX ? XRevConvert(centerX - halfWidth) : leftBottom.x,
      y: doZoomY ? YRevConvert(centerY - halfHeight) : leftBottom.y,
    };
    const nextRightTop = {
      x: doZoomX ? XRevConvert(centerX + halfWidth) : rightTop.x,
      y: doZoomY ? YRevConvert(centerY + halfHeight) : rightTop.y,
    };

    setLimitLeftBottom(nextLeftBottom);
    setLimitRightTop(nextRightTop);
  };
  // 描画フックを呼び出し
  useDrawGraph({
    canvasRef,
    leftBottom,
    rightTop,
    datas,
    xLabel,
    yLabel,
    height
  });
  useEffect(() => {
    const fetchLogin = async()=>{
      await loginLog()
    }

    fetchLogin()
    const elem = graphRef.current;

    const wheelHandler = (e) => {
      e.preventDefault();
      // zoom
    };

    elem.addEventListener("wheel", wheelHandler, {
      passive: false,
    });

    return () => elem.removeEventListener("wheel", wheelHandler);
  }, []);
  //height変化時の凡例位置の整合性
  useEffect(()=>{
    const legendWidth = legendRef.current?.clientWidth ?? 0;
    const legendHeight = legendRef.current?.clientHeight ?? 0;
    setLegendState((prev) => ({
      ...prev,
      x: Math.min(
        Math.max(prev.x, 0),
        graphRef.current.clientWidth - legendWidth - 97,
      ),
      y: Math.min(
        Math.max(prev.y, 0),
        graphRef.current.clientHeight*height/100 - legendHeight - 80,
      ),
    }));
  },[height,datas])
  return (
    <div className="App">
      <div
        style={{
          display: "flex",
          width: "100%",
          height: "95vh",
          userSelect: "none",
        }}
      >
        <div
          ref={graphRef}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerLeave={onPointerLeave}
          onWheel={onWheel}
          style={{
            flex: 3,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: "white",
            margin: 10,
            touchAction: "none",
          }}
        >
          <Graph
            canvasRef={canvasRef}
            legendRef={legendRef}
            leftBottom={leftBottom}
            rightTop={rightTop}
            xLabel={xLabel}
            yLabel={yLabel}
            datas={datas}
            legendState={legendState}
            zoomVelosity = {zoomVelosity}
            axisFontSize = {axisFontSize}
            hoveredAxis={hoveredAxis}
            height={height}
          />
        </div>

        <div
          style={{
            flex: 2,
            overflowY: "auto",
            padding: 10,
          }}
        >
          <Inputer
            graphRef={graphRef}
            leftBottom={leftBottom}
            setLimitLeftBottom={setLimitLeftBottom}
            rightTop={rightTop}
            setLimitRightTop={setLimitRightTop}
            xLabel={xLabel}
            setXLabel={setXLabel}
            yLabel={yLabel}
            setYLabel={setYLabel}
            datas={datas}
            setDatas={setDatas}
            legendState={legendState}
            setLegendState={setLegendState}
            zoomVelosity = {zoomVelosity}
            setZoomVelosity = {setZoomVelosity}
            axisFontSize = {axisFontSize}
            setAxisFontSize = {setAxisFontSize}
            onLogAction={recordLog}
            legendRef={legendRef}
            height = {height}
            setHeight = {setHeight}
          />
        </div>
      </div>
      <Modal/>
    </div>
  );
}

export default App;
